/* eslint-disable no-unused-vars */
// ScheduleInterviewScreen_FullyIntegrated.js
// Comprehensive production-ready interview scheduling screen with advanced features,
// calendar integration, availability management, team coordination, and professional interface

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Modal,
  ActivityIndicator,
  Dimensions,
  Alert,
  FlatList,
  RefreshControl,
  Animated,
  StatusBar,
  Switch,
  KeyboardAvoidingView,
  Platform,
  Image,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  AntDesign,
  MaterialIcons,
  Feather,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) params.set('company_id', companyId);
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====


const { width, height } = Dimensions.get('window');

const ScheduleInterviewScreen_FullyIntegrated = ({ route, navigation }) => {
  // Get candidate data from route params.
  // candidateId is null when missing — DO NOT default to a string like
  // 'default', that produces malformed /api/data/business_candidates?id=default
  // requests which 404 on the server.
  // Be tolerant of how callers name the id param — different parts of the
  // app use candidateId, applicantId, or applicationId for the same thing
  // (an applications.id). Accept whichever the caller provides; fall back
  // through them in order. Same for the prefilled record (applicant/candidate).
  const candidateId =
    route?.params?.candidateId ||
    route?.params?.applicantId ||
    route?.params?.applicationId ||
    route?.params?.applicant?.id ||
    route?.params?.candidate?.id ||
    null;
  const initialCandidateData =
    route?.params?.candidateData ||
    route?.params?.applicant ||
    route?.params?.candidate ||
    null;
  const jobId = route?.params?.jobId || route?.params?.job_id || null;
  const editingInterview = route?.params?.editingInterview || route?.params?.interview || null;

  // State management
  const [candidate, setCandidate] = useState(null);
  const [job, setJob] = useState(null);
  const [interviewers, setInterviewers] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [totalSteps] = useState(5);


  // ── Company logo loader (auto-injected) ─────────────────────────────────────
  // Hydrates from cache for instant paint, then refreshes from
  // /api/company-profile/:id so any logo / name change made elsewhere
  // (Settings, Dashboard, etc.) propagates to this screen on next open.
  const [_companyLogo,  _setCompanyLogo]  = useState(null);
  const [_companyName,  _setCompanyName]  = useState('');
  // Full company profile — used for the rich details banner under the header.
  const [_companyProfile, _setCompanyProfile] = useState(null);
  useEffect(() => { _loadCompanyLogo(); }, []);
  const _loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        _setCompanyLogo(profile.logo || null);
        if (profile.name || profile.company_name) _setCompanyName(profile.name || profile.company_name);
        _setCompanyProfile(profile);
      }
      const cid = await AsyncStorage.getItem('company_id');
      if (cid) {
        try {
          const res = await fetch(`https://api.jobeng.ai/api/company-profile/${cid}`, {
            headers: { 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' }
          });
          if (res.ok) {
            const json = await res.json();
            const row = (json?.data && !Array.isArray(json.data)) ? json.data
                     : (Array.isArray(json?.data) ? json.data[0] : json);
            if (row && (row.logo || row.name || row.company_name)) {
              if (row.logo) _setCompanyLogo(row.logo);
              const nm = row.name || row.company_name;
              if (nm) _setCompanyName(nm);
              _setCompanyProfile(row);
              await AsyncStorage.setItem('company_profile', JSON.stringify(row));
              console.log('[CompanyLogo] refreshed from API for cid=' + cid);
            }
          }
        } catch (e) { /* offline — cache is enough */ }
      }
    } catch (e) { console.warn('Logo load error:', e.message); }
  };
  // Modal states
  const [showCalendar, setShowCalendar] = useState(false);
  // Track which month the calendar modal is viewing (independent of the
  // selected date — lets the user browse forward/back through months).
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [showInterviewerSelection, setShowInterviewerSelection] = useState(false);
  const [showTimeSlots, setShowTimeSlots] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  // Real applicants pulled from the recruitment pipeline. Populated when the
  // screen is opened without a pre-selected candidate (e.g. from a dashboard
  // shortcut) so the recruiter can pick one inline instead of staring at an
  // empty form.
  const [availableCandidates, setAvailableCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [showJobModal, setShowJobModal] = useState(false);

  // Form data
  const [interviewData, setInterviewData] = useState({
    id: editingInterview?.id || null,
    candidateId: candidateId,
    jobId: jobId,
    title: editingInterview?.title || '',
    type: editingInterview?.type || 'technical',
    format: editingInterview?.format || 'video',
    duration: editingInterview?.duration || 60,
    date: editingInterview?.date || '',
    time: editingInterview?.time || '',
    timezone: editingInterview?.timezone || 'GMT',
    location: editingInterview?.location || '',
    meetingLink: editingInterview?.meetingLink || '',
    selectedInterviewers: editingInterview?.selectedInterviewers || [],
    questions: editingInterview?.questions || [],
    notes: editingInterview?.notes || '',
    instructions: editingInterview?.instructions || '',
    reminderEnabled: editingInterview?.reminderEnabled ?? true,
    recordingEnabled: editingInterview?.recordingEnabled ?? true,
    transcriptionEnabled: editingInterview?.transcriptionEnabled ?? false,
    aiAnalysisEnabled: editingInterview?.aiAnalysisEnabled ?? false,
    priority: editingInterview?.priority || 'medium',
    isUrgent: editingInterview?.isUrgent ?? false,
    requiresPreparation: editingInterview?.requiresPreparation ?? false,
    preparationTime: editingInterview?.preparationTime || 15,
    bufferTime: editingInterview?.bufferTime || 15,
    maxReschedules: editingInterview?.maxReschedules || 2,
    autoConfirm: editingInterview?.autoConfirm ?? false,
    sendCalendarInvite: editingInterview?.sendCalendarInvite ?? true,
    notifyTeam: editingInterview?.notifyTeam ?? true,
    status: editingInterview?.status || 'scheduled',
    customFields: editingInterview?.customFields || {},
  });

  // Calendar data
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [timeSlots, setTimeSlots] = useState([]);
  const [workingHours, setWorkingHours] = useState({
    start: '09:00',
    end: '17:00',
    timezone: 'GMT',
    workingDays: [1, 2, 3, 4, 5], // Monday to Friday
  });

  // Interview types and formats
  const interviewTypes = [
    { id: 'screening', name: 'Phone Screening', icon: 'phone', color: '#3B82F6', duration: 30 },
    { id: 'technical', name: 'Technical Interview', icon: 'code-braces', color: '#10B981', duration: 90 },
    { id: 'behavioral', name: 'Behavioral Interview', icon: 'account-heart', color: '#8B5CF6', duration: 60 },
    { id: 'system_design', name: 'System Design', icon: 'sitemap', color: '#F59E0B', duration: 120 },
    { id: 'cultural_fit', name: 'Cultural Fit', icon: 'account-group', color: '#EF4444', duration: 45 },
    { id: 'final', name: 'Final Interview', icon: 'account-tie', color: '#06B6D4', duration: 60 },
    { id: 'panel', name: 'Panel Interview', icon: 'account-multiple', color: '#EC4899', duration: 90 },
    { id: 'presentation', name: 'Presentation', icon: 'presentation', color: '#84CC16', duration: 75 },
  ];

  const interviewFormats = [
    { id: 'video', name: 'Video Call', icon: 'video', description: 'Online video interview' },
    { id: 'phone', name: 'Phone Call', icon: 'phone', description: 'Voice-only interview' },
    { id: 'in_person', name: 'In Person', icon: 'account-group', description: 'Face-to-face meeting' },
    { id: 'hybrid', name: 'Hybrid', icon: 'laptop', description: 'Mix of online and in-person' },
  ];

  const priorityLevels = [
    { id: 'low', name: 'Low Priority', color: '#10B981', description: 'Standard scheduling' },
    { id: 'medium', name: 'Medium Priority', color: '#F59E0B', description: 'Preferred scheduling' },
    { id: 'high', name: 'High Priority', color: '#EF4444', description: 'Urgent scheduling' },
    { id: 'critical', name: 'Critical', color: '#DC2626', description: 'Immediate attention' },
  ];

  const timezones = [
    { id: 'GMT', name: 'GMT (London)', offset: '+00:00' },
    { id: 'EST', name: 'EST (New York)', offset: '-05:00' },
    { id: 'PST', name: 'PST (Los Angeles)', offset: '-08:00' },
    { id: 'CET', name: 'CET (Berlin)', offset: '+01:00' },
    { id: 'JST', name: 'JST (Tokyo)', offset: '+09:00' },
    { id: 'AEST', name: 'AEST (Sydney)', offset: '+10:00' },
  ];

  // Load data on component mount
  useEffect(() => {
    loadInitialData();
  }, [candidateId, jobId]);

  // Load initial data
  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      await Promise.all([
        loadCandidate(),
        loadJob(),
        loadInterviewers(),
        loadAvailableSlots(),
        generateTimeSlots(),
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      Alert.alert('Error', 'Failed to load data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Load candidate data
  // Hits the real /api/recruitment/applicant/:id endpoint we added — returns
  // the application row JOINed with users + jobs + companies + inline
  // interviews/offers. Replaces the non-existent business-candidates route.
  // Normalize a raw application/candidate row into the camelCase shape the
  // rest of this screen reads. Used both when an applicant is passed via
  // route.params (from ApplicantDetailsScreen, ContactCandidateScreen, etc)
  // and when we fetch it ourselves from /api/recruitment/applicant/:id.
  const shapeCandidate = (row) => {
    if (!row) return null;
    return {
      id:       row.id || row.application_id,
      application_id: row.application_id || row.id,
      // Accept both camelCase (already-normalised input) and snake_case (raw DB row)
      name:     row.name     || row.candidate_name  || row.full_name || 'Applicant',
      email:    row.email    || row.candidate_email || '',
      phone:    row.phone    || row.candidate_phone || '',
      avatar:   row.avatar   || row.candidate_avatar || row.avatar_url || null,
      location: row.location || row.candidate_location || '',
      currentTitle:   row.currentTitle   || row.current_title   || '',
      currentCompany: row.currentCompany || row.current_company || '',
      jobId:    row.jobId    || row.job_id,
      jobTitle: row.jobTitle || row.job_title || '',
      stage:    row.stage  || 'new',
      status:   row.status || 'submitted',
      skills:   Array.isArray(row.skills) ? row.skills : [],
      experience: row.experience || row.experience_years || 0,
      raw: row.raw || row,
    };
  };

  const loadCandidate = async () => {
    try {
      if (initialCandidateData) {
        // Came in from ApplicantDetails / ContactCandidate / Pipeline. Always
        // reshape — the source may be a raw application row (snake_case) or
        // an already-shaped candidate, and shapeCandidate handles both.
        setCandidate(shapeCandidate(initialCandidateData));
        // If a job_id is on the row, populate job state too so step 2 isn't blank
        const r = initialCandidateData;
        const jobId = r.jobId || r.job_id;
        const jobTitle = r.jobTitle || r.job_title;
        if (jobId && jobTitle) {
          setJob({
            id: jobId,
            title: jobTitle,
            location: r.jobLocation || r.job_location || '',
            salary_min: r.job_salary_min,
            salary_max: r.job_salary_max,
            description: r.jobDescription || r.job_description || '',
          });
        }
        return;
      }

      if (!candidateId) {
        // Opened with no preselected candidate (likely a dashboard shortcut or
        // FAB tap). Instead of showing an empty form, fetch the company's
        // active pipeline and let the recruiter pick a candidate inline.
        await loadAvailableCandidates();
        setShowCandidateModal(true);
        return;
      }

      // candidateId here is actually the application id (recruiter clicks
      // "Schedule Interview" on an applicant card, which is an application row).
      const token = await AsyncStorage.getItem('auth_token').catch(() => null);
      try {
        const res = await fetch(`${API_BASE_URL}/api/recruitment/applicant/${candidateId}`, {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (res.ok) {
          const row = await res.json();
          if (row && !row.error) {
            setCandidate(shapeCandidate(row));
            // If we have job info from the JOIN, populate the job state too
            if (row.job_id) {
              setJob({
                id: row.job_id,
                title: row.job_title,
                location: row.job_location,
                salary_min: row.job_salary_min,
                salary_max: row.job_salary_max,
                description: row.job_description,
              });
            }
            return;
          }
        }
      } catch (apiErr) {
        console.warn('candidate API:', apiErr?.message);
      }

      // Local cache fallback (offline mode) — but never mock data
      const stored = await AsyncStorage.getItem('businessCandidates');
      if (stored) {
        try {
          const candidates = JSON.parse(stored);
          const found = candidates.find(c => String(c.id) === String(candidateId));
          if (found) { setCandidate(shapeCandidate(found)); return; }
        } catch (_) {}
      }
      // No data found — show empty state rather than mock data
      setCandidate(null);
    } catch (error) {
      console.warn('loadCandidate:', error?.message);
      setCandidate(null);
    }
  };

  // ──────────────────────────────────────────────────────────────────────
  // Candidate picker — used when the screen is opened without a preselected
  // applicant. Fetches the company pipeline (real DB, JOINed with users +
  // jobs) and shows the list in a modal. Picking a candidate populates the
  // form just as if they'd navigated in from ApplicantDetailsScreen.
  // ──────────────────────────────────────────────────────────────────────
  const loadAvailableCandidates = async () => {
    setLoadingCandidates(true);
    try {
      const cid = parseInt(await AsyncStorage.getItem('company_id') || '0', 10);
      if (!cid) { setAvailableCandidates([]); return; }
      const token = await AsyncStorage.getItem('auth_token').catch(() => null);
      const res = await fetch(
        `${API_BASE_URL}/api/recruitment/pipeline?company_id=${cid}&limit=200`,
        { headers: {
            'Content-Type': 'application/json',
            'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        } }
      );
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const rows = await res.json();
      const list = (Array.isArray(rows) ? rows : (rows?.data || [])).map(row => ({
        id: row.id,
        application_id: row.id,
        name:    row.candidate_name || 'Applicant',
        email:   row.candidate_email || '',
        phone:   row.candidate_phone || '',
        avatar:  row.candidate_avatar || null,
        location: row.candidate_location || '',
        jobId:   row.job_id,
        jobTitle: row.job_title || '',
        stage:   row.stage || 'new',
        currentTitle: row.current_title || '',
        currentCompany: row.current_company || '',
        skills:  Array.isArray(row.skills) ? row.skills : [],
        experience: row.experience_years || 0,
        interviewsScheduled: Number(row.interviews_scheduled || 0),
        raw: row,
      }));
      setAvailableCandidates(list);
    } catch (e) {
      console.warn('loadAvailableCandidates:', e?.message);
      setAvailableCandidates([]);
    } finally {
      setLoadingCandidates(false);
    }
  };

  // User picked a candidate from the modal — populate the form fields and
  // also load the matching job so the prefill stays consistent with the
  // normal entry path.
  const handleCandidatePick = (picked) => {
    setCandidate(picked);
    setShowCandidateModal(false);
    if (picked.jobId) {
      setJob({
        id: picked.jobId,
        title: picked.jobTitle,
        location: picked.raw?.job_location,
        salary_min: picked.raw?.job_salary_min,
        salary_max: picked.raw?.job_salary_max,
        description: picked.raw?.job_description,
      });
    }
  };

  // Load job data
  // Pulls from /api/data/jobs/:id — the real jobs table. The previous
  // version queried 'business-jobs' which doesn't exist, silently failed,
  // and substituted a mock job. Now we read the actual posting.
  const loadJob = async () => {
    if (!jobId) return;
    try {
      const token = await AsyncStorage.getItem('auth_token').catch(() => null);
      const res = await fetch(`${API_BASE_URL}/api/data/jobs/${jobId}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (res.ok) {
        const body = await res.json();
        // /api/data/:table/:id can return the row directly or wrapped — handle both
        const row = body?.data || body;
        if (row && row.id) {
          setJob({
            id: row.id,
            title: row.title,
            department: row.department,
            location: row.location,
            employmentType: row.employment_type || row.job_type,
            salary_min: row.salary_min,
            salary_max: row.salary_max,
            description: row.description,
            requirements: row.requirements,
          });
          return;
        }
      }
      // Offline cache only — never mock
      const stored = await AsyncStorage.getItem('businessJobs');
      if (stored) {
        try {
          const jobs = JSON.parse(stored);
          const found = jobs.find(j => String(j.id) === String(jobId));
          if (found) { setJob(found); return; }
        } catch (_) {}
      }
      setJob(null);
    } catch (error) {
      console.warn('loadJob:', error?.message);
      setJob(null);
    }
  };

  // Load interviewers from the company's employee list
  // Real source: /api/data/employees?company_id=X. The previous version
  // POST'd fake interviewers to a non-existent 'business-interviewers' table
  // every time the screen opened, leaving stale rows nowhere useful.
  const loadInterviewers = async () => {
    try {
      const companyId = parseInt(await AsyncStorage.getItem('company_id') || '0', 10);
      if (!companyId) { setInterviewers([]); return; }
      const token = await AsyncStorage.getItem('auth_token').catch(() => null);
      // Try employees first — that's the canonical "people who can interview"
      // list. Fall back to users-by-company if employees doesn't exist yet.
      let rows = [];
      try {
        const res = await fetch(`${API_BASE_URL}/api/data/employees?company_id=${companyId}&limit=100`, {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (res.ok) {
          const body = await res.json();
          rows = Array.isArray(body) ? body : (Array.isArray(body?.data) ? body.data : []);
        }
      } catch (_) {}

      const mapped = rows.map(e => ({
        id: e.id,
        name: e.full_name || e.name || `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.email || 'Team member',
        title: e.job_title || e.position || e.title || '',
        role: e.job_title || e.position || e.title || '',
        email: e.email || '',
        avatar: e.avatar_url || e.profile_image || null,
        department: e.department || '',
        // expertise stays undefined — real employees don't have this field.
        // The picker handles its absence gracefully.
      }));

      // If we got no rows from employees, fall back to current user being the
      // only available interviewer — so the form still works.
      if (mapped.length === 0) {
        const cached = await AsyncStorage.getItem('user_profile');
        if (cached) {
          try {
            const u = JSON.parse(cached);
            mapped.push({
              id: u.id || 'me',
              name: u.full_name || u.name || 'You',
              title: u.job_title || 'Recruiter',
              role: u.job_title || 'Recruiter',
              email: u.email || '',
              avatar: u.avatar_url || null,
              department: '',
            });
          } catch (_) {}
        }
      }
      setInterviewers(mapped);
      await AsyncStorage.setItem('businessInterviewers', JSON.stringify(mapped)).catch(() => {});
    } catch (error) {
      console.warn('loadInterviewers:', error?.message);
      // Offline fallback only
      try {
        const stored = await AsyncStorage.getItem('businessInterviewers');
        if (stored) setInterviewers(JSON.parse(stored));
        else setInterviewers([]);
      } catch { setInterviewers([]); }
    }
  };

  // Generate available time slots
  // These are CLIENT-SIDE only — there's no calendar-availability table.
  // We surface a sensible default grid (next 14 weekdays × business hours)
  // and let the recruiter pick. Server-side availability checking, if needed
  // later, would query application_interviews for conflicts; for now an empty
  // slot list is fine because the form also has a free date/time picker.
  const loadAvailableSlots = async () => {
    try {
      const slots = [];
      const now = new Date();
      const hours = [9, 10, 11, 13, 14, 15, 16];   // skip noon
      let dayOffset = 1;
      while (slots.length < 25 && dayOffset < 30) {
        const d = new Date(now);
        d.setDate(d.getDate() + dayOffset);
        const dow = d.getDay();
        // Skip weekends
        if (dow !== 0 && dow !== 6) {
          for (const h of hours) {
            const slot = new Date(d);
            slot.setHours(h, 0, 0, 0);
            slots.push({
              id: `slot_${slot.toISOString()}`,
              date: slot.toISOString().slice(0, 10),
              time: `${String(h).padStart(2, '0')}:00`,
              datetime: slot.toISOString(),
              dayLabel: slot.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }),
              timeLabel: `${h > 12 ? h - 12 : h}:00 ${h >= 12 ? 'PM' : 'AM'}`,
              available: true,
            });
            if (slots.length >= 25) break;
          }
        }
        dayOffset++;
      }
      setAvailableSlots(slots);
    } catch (error) {
      console.warn('loadAvailableSlots:', error?.message);
      setAvailableSlots([]);
    }
  };

  // Generate time slots for selected date
  const generateTimeSlots = () => {
    const slots = [];
    const startHour = parseInt(workingHours.start.split(':')[0]);
    const endHour = parseInt(workingHours.end.split(':')[0]);
    
    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const isAvailable = Math.random() > 0.3; // 70% availability
        
        slots.push({
          time: timeString,
          available: isAvailable,
          interviewer: isAvailable ? interviewers[Math.floor(Math.random() * interviewers.length)]?.name : null,
        });
      }
    }
    
    setTimeSlots(slots);
  };

  // Generate mock data
  const generateMockCandidate = () => ({
    id: candidateId,
    name: 'John Smith',
    email: 'john.smith@email.com',
    phone: '+44 7700 900123',
    position: 'Senior Software Engineer',
    experience: '5 years',
    skills: 'React, Node.js, Python, AWS',
    location: 'London, UK',
    timezone: 'GMT',
    preferredTimes: ['09:00-12:00', '14:00-17:00'],
    availability: 'Weekdays 9-5',
    notes: 'Experienced full-stack developer with strong React and Node.js skills',
  });

  const generateMockJob = () => ({
    id: jobId || 'job_1',
    title: 'Senior Software Engineer',
    department: 'Engineering',
    location: 'London, UK',
    type: 'Full-time',
    description: 'We are looking for a senior software engineer to join our team',
    requirements: ['5+ years experience', 'React expertise', 'Node.js knowledge'],
    salary: '£70,000 - £90,000',
    benefits: ['Health insurance', 'Pension', 'Flexible working'],
  });

  const generateMockInterviewers = () => [
    {
      id: 'int_1',
      name: 'Sarah Johnson',
      title: 'Engineering Manager',
      department: 'Engineering',
      email: 'sarah.johnson@company.com',
      phone: '+44 7700 900456',
      expertise: ['Technical Leadership', 'System Design', 'Team Management'],
      availability: {
        monday: ['09:00-12:00', '14:00-17:00'],
        tuesday: ['09:00-12:00', '14:00-17:00'],
        wednesday: ['09:00-12:00', '14:00-17:00'],
        thursday: ['09:00-12:00', '14:00-17:00'],
        friday: ['09:00-12:00', '14:00-16:00'],
      },
      timezone: 'GMT',
      preferredFormats: ['video', 'in_person'],
      maxInterviewsPerDay: 4,
      rating: 4.8,
      totalInterviews: 156,
      isActive: true,
    },
    {
      id: 'int_2',
      name: 'Mike Brown',
      title: 'Senior Developer',
      department: 'Engineering',
      email: 'mike.brown@company.com',
      phone: '+44 7700 900789',
      expertise: ['Frontend Development', 'React', 'JavaScript'],
      availability: {
        monday: ['10:00-12:00', '14:00-18:00'],
        tuesday: ['10:00-12:00', '14:00-18:00'],
        wednesday: ['10:00-12:00', '14:00-18:00'],
        thursday: ['10:00-12:00', '14:00-18:00'],
        friday: ['10:00-12:00', '14:00-17:00'],
      },
      timezone: 'GMT',
      preferredFormats: ['video', 'phone'],
      maxInterviewsPerDay: 3,
      rating: 4.6,
      totalInterviews: 89,
      isActive: true,
    },
    {
      id: 'int_3',
      name: 'Emily Davis',
      title: 'Product Manager',
      department: 'Product',
      email: 'emily.davis@company.com',
      phone: '+44 7700 900012',
      expertise: ['Product Strategy', 'User Experience', 'Agile'],
      availability: {
        monday: ['09:00-11:00', '15:00-17:00'],
        tuesday: ['09:00-11:00', '15:00-17:00'],
        wednesday: ['09:00-11:00', '15:00-17:00'],
        thursday: ['09:00-11:00', '15:00-17:00'],
        friday: ['09:00-11:00', '15:00-16:00'],
      },
      timezone: 'GMT',
      preferredFormats: ['video', 'in_person'],
      maxInterviewsPerDay: 3,
      rating: 4.9,
      totalInterviews: 234,
      isActive: true,
    },
    {
      id: 'int_4',
      name: 'David Wilson',
      title: 'CTO',
      department: 'Engineering',
      email: 'david.wilson@company.com',
      phone: '+44 7700 900345',
      expertise: ['Technology Strategy', 'Architecture', 'Leadership'],
      availability: {
        tuesday: ['11:00-12:00', '16:00-17:00'],
        wednesday: ['11:00-12:00', '16:00-17:00'],
        thursday: ['11:00-12:00', '16:00-17:00'],
        friday: ['11:00-12:00'],
      },
      timezone: 'GMT',
      preferredFormats: ['video', 'in_person'],
      maxInterviewsPerDay: 2,
      rating: 5.0,
      totalInterviews: 67,
      isActive: true,
    },
    {
      id: 'int_5',
      name: 'Lisa Anderson',
      title: 'HR Manager',
      department: 'Human Resources',
      email: 'lisa.anderson@company.com',
      phone: '+44 7700 900678',
      expertise: ['Behavioral Assessment', 'Cultural Fit', 'Onboarding'],
      availability: {
        monday: ['09:00-12:00', '13:00-17:00'],
        tuesday: ['09:00-12:00', '13:00-17:00'],
        wednesday: ['09:00-12:00', '13:00-17:00'],
        thursday: ['09:00-12:00', '13:00-17:00'],
        friday: ['09:00-12:00', '13:00-16:00'],
      },
      timezone: 'GMT',
      preferredFormats: ['video', 'phone', 'in_person'],
      maxInterviewsPerDay: 5,
      rating: 4.7,
      totalInterviews: 312,
      isActive: true,
    },
  ];

  const generateMockAvailableSlots = () => {
    const slots = [];
    const today = new Date();
    
    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      // Skip weekends
      if (date.getDay() === 0 || date.getDay() === 6) continue;
      
      const daySlots = [];
      for (let hour = 9; hour < 17; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          if (Math.random() > 0.4) { // 60% availability
            daySlots.push({
              time: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
              available: true,
              interviewerId: interviewers[Math.floor(Math.random() * interviewers.length)]?.id,
            });
          }
        }
      }
      
      slots.push({
        date: date.toISOString().split('T')[0],
        slots: daySlots,
      });
    }
    
    return slots;
  };

  // Update interview data
  const updateInterviewData = (field, value) => {
    setInterviewData(prev => ({ ...prev, [field]: value }));
  };

  // Handle step navigation
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const previousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Validate current step
  const validateStep = () => {
    switch (currentStep) {
      case 1:
        return interviewData.title && interviewData.type && interviewData.format;
      case 2:
        return candidate && (job || interviewData.title);
      case 3:
        return interviewData.date && interviewData.time && interviewData.selectedInterviewers.length > 0;
      case 4:
        return true; // Settings are optional
      case 5:
        return true; // Review step
      default:
        return false;
    }
  };

  // Save interview
  // Writes to the canonical application_interviews table — same row that the
  // candidate's VideoInterviewScreen reads from. The previous implementation
  // posted to a non-existent business-interviews route and only persisted in
  // AsyncStorage, so the candidate never saw the interview.
  const saveInterview = async () => {
    try {
      setIsSaving(true);

      // Combine date (yyyy-mm-dd) + time (hh:mm) into a single ISO timestamp.
      // Defensive: if either is missing, fall back to NOW so the row still
      // saves rather than silently dropping the interview.
      const buildScheduledAt = () => {
        try {
          if (!interviewData.date || !interviewData.time) return new Date().toISOString();
          const d = String(interviewData.date).slice(0, 10);
          const t = String(interviewData.time).slice(0, 5);
          return new Date(`${d}T${t}:00`).toISOString();
        } catch { return new Date().toISOString(); }
      };

      // Map selected interviewer ids → JSONB array the application_interviews
      // table expects: [{ id, name, role }]
      const panel = (interviewData.selectedInterviewers || []).map(id => {
        const intv = interviewers.find(i => String(i.id) === String(id));
        return intv ? { id: intv.id, name: intv.name, role: intv.role || intv.title || '' } : null;
      }).filter(Boolean);

      // Application id — candidateId param here is actually the application
      // id (recruiter clicks "Schedule Interview" on an applicant card which
      // passes the application row's id).
      const applicationId = parseInt(candidateId, 10) || candidate?.application_id || candidate?.id;
      if (!applicationId) {
        Alert.alert('Cannot save', 'No application id was supplied to this screen.');
        setIsSaving(false);
        return;
      }

      // For video interviews without a meeting link, mint a Jitsi room. The
      // candidate side (VideoInterviewScreen) and recruiter side (VideoScreen)
      // both join the same room via its URL — this is what makes the meeting
      // actually happen. Without a link, "video" interview is just a label.
      const isVideo = (interviewData.type || 'video') === 'video'
                   || (interviewData.format === 'video')
                   || (interviewData.format === 'online');
      let meetingLink = interviewData.meetingLink;
      if (isVideo && !meetingLink) {
        const roomId = `jobeng-${applicationId}-${Date.now().toString(36)}`;
        meetingLink = `https://meet.jit.si/${roomId}`;
      }

      const payload = {
        application_id:   applicationId,
        interview_type:   interviewData.type || 'video',
        scheduled_date:   buildScheduledAt(),
        duration_minutes: Number(interviewData.duration) || 60,
        meeting_link:     meetingLink || null,
        location:         interviewData.location || null,
        interviewer_name: panel[0]?.name || null,
        // interviewers is a JSONB column. The generic /api/data/:table handler
        // passes parameters straight to PostgreSQL without auto-stringifying
        // objects, so PG sees "[object Object]" and rejects it as
        // "invalid input syntax for type json". Pre-stringify so it goes in as
        // a valid JSON document.
        interviewers:     panel.length ? JSON.stringify(panel) : null,
        instructions:     interviewData.instructions || interviewData.notes || null,
        round_number:     interviewData.round || 1,
        recording_enabled: !!interviewData.recordingEnabled,
        status:           interviewData.status || 'scheduled',
      };

      // PUT if editing, POST if creating
      const editingId = interviewData.id && !String(interviewData.id).startsWith('interview_')
        ? interviewData.id : null;
      const url = editingId
        ? `${API_BASE_URL}/api/data/application_interviews/${editingId}`
        : `${API_BASE_URL}/api/data/application_interviews`;
      const method = editingId ? 'PUT' : 'POST';

      const token = await AsyncStorage.getItem('auth_token').catch(() => null);
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error(`Server returned ${res.status}: ${errText.slice(0, 200)}`);
      }
      const saved = await res.json().catch(() => ({}));

      // Also bump the application stage to "interviewed" so the pipeline view
      // reflects the change immediately.
      try {
        await fetch(`${API_BASE_URL}/api/recruitment/applicant/${applicationId}/stage`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ stage: 'interviewed', note: `Interview scheduled for ${interviewData.date} ${interviewData.time}` }),
        });
      } catch (_) { /* non-fatal */ }

      // Keep a local mirror for offline reads
      try {
        const existingInterviews = await AsyncStorage.getItem('businessInterviews');
        const interviewsList = existingInterviews ? JSON.parse(existingInterviews) : [];
        const mirror = { ...payload, id: saved.id || editingId || `interview_${Date.now()}`, createdAt: new Date().toISOString() };
        const idx = interviewsList.findIndex(i => String(i.id) === String(mirror.id));
        if (idx >= 0) interviewsList[idx] = mirror; else interviewsList.push(mirror);
        await AsyncStorage.setItem('businessInterviews', JSON.stringify(interviewsList));
      } catch (_) {}

      // Build the success dialog. Video interviews get a "Start now" button
      // that hands the new row off to VideoScreen — same flow you'd use later
      // from ApplicantDetails → Interviews tab → Join. For non-video types
      // we just confirm and close.
      const savedInterviewId = saved?.id || editingId;
      const goToVideo = () => {
        navigation.navigate('VideoScreen', {
          applicationId,
          interviewId:    savedInterviewId,
          interviewType:  interviewData.type || 'video',
          meetingUrl:     meetingLink,
          candidate:      candidate || {
            id:     applicationId,
            name:   candidate?.name || 'Candidate',
            email:  candidate?.email || '',
          },
          interviewers:   panel,
        });
      };

      const buttons = [{ text: 'Done', onPress: () => navigation?.goBack?.() }];
      if (isVideo && meetingLink) {
        buttons.unshift({
          text: 'Start interview now',
          onPress: goToVideo,
        });
      }

      Alert.alert(
        'Success',
        editingId
          ? `Interview updated.${isVideo ? ' You can start it now or rejoin later from the candidate\'s profile.' : ''}`
          : `Interview scheduled.${isVideo ? ' Tap "Start interview now" to launch the video call, or rejoin later from the candidate\'s profile.' : ''}`,
        buttons,
        { cancelable: true }
      );
    } catch (error) {
      console.warn('saveInterview:', error?.message);
      Alert.alert('Could not save interview', error?.message || 'Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Load interview template
  const loadTemplate = (templateType) => {
    const templates = {
      technical: {
        title: 'Technical Interview',
        type: 'technical',
        duration: 90,
        questions: [
          'Tell me about your experience with React.js',
          'How do you handle state management in large applications?',
          'Explain the difference between SQL and NoSQL databases',
          'Walk me through your approach to debugging complex issues',
        ],
        instructions: 'Focus on technical skills, problem-solving abilities, and coding experience.',
        notes: 'Prepare coding challenges and technical scenarios.',
      },
      behavioral: {
        title: 'Behavioral Interview',
        type: 'behavioral',
        duration: 60,
        questions: [
          'Tell me about a time when you had to work under pressure',
          'Describe a situation where you had to resolve a conflict with a team member',
          'How do you handle feedback and criticism?',
          'What motivates you in your work?',
        ],
        instructions: 'Assess cultural fit, communication skills, and work style.',
        notes: 'Use STAR method for behavioral questions.',
      },
      final: {
        title: 'Final Interview',
        type: 'final',
        duration: 60,
        questions: [
          'Why do you want to work for our company?',
          'Where do you see yourself in 5 years?',
          'What are your salary expectations?',
          'Do you have any questions for us?',
        ],
        instructions: 'Final assessment and mutual fit evaluation.',
        notes: 'Discuss compensation, benefits, and next steps.',
      },
    };

    const template = templates[templateType];
    if (template) {
      setInterviewData(prev => ({ ...prev, ...template }));
      setShowTemplateModal(false);
      Alert.alert('Success', 'Template loaded successfully!');
    }
  };

  // Refresh data
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadInitialData();
    setRefreshing(false);
  }, []);

  // Render step indicator
  const renderStepIndicator = () => (
    <View style={styles.stepIndicator}>
      {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
        <View key={step} style={styles.stepContainer}>
          <View style={[
            styles.stepCircle,
            currentStep >= step && styles.activeStepCircle,
            currentStep > step && styles.completedStepCircle,
          ]}>
            {currentStep > step ? (
              <MaterialCommunityIcons name="check" size={16} color="#ffffff" />
            ) : (
              <Text style={[
                styles.stepNumber,
                currentStep >= step && styles.activeStepNumber,
              ]}>
                {step}
              </Text>
            )}
          </View>
          <Text style={[
            styles.stepLabel,
            currentStep >= step && styles.activeStepLabel,
          ]}>
            <Text>{step === 1 ? 'Type' : step === 2 ? 'Details' : step === 3 ? 'Schedule' : step === 4 ? 'Settings' : 'Review'}</Text>
          </Text>
          {step < totalSteps && <View style={[
            styles.stepConnector,
            currentStep > step && styles.activeStepConnector,
          ]} />}
        </View>
      ))}
    </View>
  );

  // Render interview type selection
  const renderTypeStep = () => (
    <ScrollView
      style={styles.stepContent}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Interview Type</Text>
        <Text style={styles.sectionDescription}>
          Select the type of interview you want to schedule
        </Text>
        
        <View style={styles.typeGrid}>
          {interviewTypes.map((type) => (
            <TouchableOpacity
              key={type.id}
              style={[
                styles.typeCard,
                interviewData.type === type.id && styles.selectedTypeCard,
                { borderColor: type.color },
              ]}
              onPress={() => {
                updateInterviewData('type', type.id);
                updateInterviewData('duration', type.duration);
                if (!interviewData.title) {
                  updateInterviewData('title', type.name);
                }
              }}
            >
              <MaterialCommunityIcons
                name={type.icon}
                size={32}
                color={interviewData.type === type.id ? '#ffffff' : type.color}
              />
              <Text style={[
                styles.typeTitle,
                interviewData.type === type.id && styles.selectedTypeTitle,
              ]}>
                {type.name}
              </Text>
              <Text style={[
                styles.typeDuration,
                interviewData.type === type.id && styles.selectedTypeDuration,
              ]}>
                {type.duration} minutes
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Interview Format</Text>
        <Text style={styles.sectionDescription}>
          Choose how the interview will be conducted
        </Text>
        
        <View style={styles.formatList}>
          {interviewFormats.map((format) => (
            <TouchableOpacity
              key={format.id}
              style={[
                styles.formatCard,
                interviewData.format === format.id && styles.selectedFormatCard,
              ]}
              onPress={() => updateInterviewData('format', format.id)}
            >
              <MaterialCommunityIcons
                name={format.icon}
                size={24}
                color={interviewData.format === format.id ? '#3b82f6' : '#6b7280'}
              />
              <View style={styles.formatInfo}>
                <Text style={[
                  styles.formatName,
                  interviewData.format === format.id && styles.selectedFormatName,
                ]}>
                  {format.name}
                </Text>
                <Text style={styles.formatDescription}>{format.description}</Text>
              </View>
              {interviewData.format === format.id && (
                <MaterialCommunityIcons name="check-circle" size={20} color="#3b82f6" />
              )}
            </TouchableOpacity>
          ))}
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Interview Title</Text>
        <TextInput
          style={styles.textInput}
          placeholder="Enter interview title"
          value={interviewData.title}
          onChangeText={(value) => updateInterviewData('title', value)}
        />
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Duration (minutes)</Text>
        <View style={styles.durationSelector}>
          {[30, 45, 60, 90, 120].map((duration) => (
            <TouchableOpacity
              key={duration}
              style={[
                styles.durationOption,
                interviewData.duration === duration && styles.selectedDurationOption,
              ]}
              onPress={() => updateInterviewData('duration', duration)}
            >
              <Text style={[
                styles.durationText,
                interviewData.duration === duration && styles.selectedDurationText,
              ]}>
                {duration}m
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Quick Templates</Text>
        <Text style={styles.sectionDescription}>
          Load a pre-configured template for common interview types
        </Text>
        
        <TouchableOpacity
          style={styles.templateButton}
          onPress={() => setShowTemplateModal(true)}
        >
          <MaterialCommunityIcons name="file-document-multiple" size={24} color="#3b82f6" />
          <Text style={styles.templateButtonText}>Choose Template</Text>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#6b7280" />
        </TouchableOpacity>
      </View>
    </ScrollView>
  );

  // Render details step
  const renderDetailsStep = () => (
    <ScrollView
      style={styles.stepContent}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Candidate Information</Text>
        
        {candidate ? (
          <View style={styles.candidateCard}>
            <View style={styles.candidateHeader}>
              <View style={styles.candidateInfo}>
                <Text style={styles.candidateName}>{candidate.name}</Text>
                <Text style={styles.candidatePosition}>{candidate.position}</Text>
                <Text style={styles.candidateLocation}>{candidate.location}</Text>
              </View>
              <TouchableOpacity
                style={styles.editButton}
                onPress={() => setShowCandidateModal(true)}
              >
                <MaterialCommunityIcons name="pencil" size={16} color="#3b82f6" />
              </TouchableOpacity>
            </View>
            
            <View style={styles.candidateDetails}>
              <View style={styles.candidateDetailItem}>
                <MaterialCommunityIcons name="email" size={16} color="#6b7280" />
                <Text style={styles.candidateDetailText}>{candidate.email}</Text>
              </View>
              <View style={styles.candidateDetailItem}>
                <MaterialCommunityIcons name="phone" size={16} color="#6b7280" />
                <Text style={styles.candidateDetailText}>{candidate.phone}</Text>
              </View>
              <View style={styles.candidateDetailItem}>
                <MaterialCommunityIcons name="clock" size={16} color="#6b7280" />
                <Text style={styles.candidateDetailText}>{candidate.timezone}</Text>
              </View>
            </View>
          </View>
        ) : (
          <TouchableOpacity
            style={styles.selectCandidateButton}
            onPress={async () => {
              if (availableCandidates.length === 0) await loadAvailableCandidates();
              setShowCandidateModal(true);
            }}
          >
            <MaterialCommunityIcons name="account-plus" size={24} color="#3b82f6" />
            <Text style={styles.selectCandidateText}>Select Candidate</Text>
          </TouchableOpacity>
        )}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Job Information</Text>
        
        {job ? (
          <View style={styles.jobCard}>
            <View style={styles.jobHeader}>
              <View style={styles.jobInfo}>
                <Text style={styles.jobTitle}>{job.title}</Text>
                <Text style={styles.jobDepartment}>{job.department}</Text>
                <Text style={styles.jobLocation}>{job.location}</Text>
              </View>
              <TouchableOpacity
                style={styles.editButton}
                onPress={() => setShowJobModal(true)}
              >
                <MaterialCommunityIcons name="pencil" size={16} color="#3b82f6" />
              </TouchableOpacity>
            </View>
            
            <View style={styles.jobDetails}>
              <View style={styles.jobDetailItem}>
                <MaterialCommunityIcons name="briefcase" size={16} color="#6b7280" />
                <Text style={styles.jobDetailText}>{job.type}</Text>
              </View>
              <View style={styles.jobDetailItem}>
                <MaterialCommunityIcons name="currency-gbp" size={16} color="#6b7280" />
                <Text style={styles.jobDetailText}>{job.salary}</Text>
              </View>
            </View>
          </View>
        ) : (
          <TouchableOpacity
            style={styles.selectJobButton}
            onPress={() => setShowJobModal(true)}
          >
            <MaterialCommunityIcons name="briefcase-plus" size={24} color="#3b82f6" />
            <Text style={styles.selectJobText}>Select Job Position</Text>
          </TouchableOpacity>
        )}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Interview Location</Text>
        
        {interviewData.format === 'video' && (
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Meeting Link</Text>
            <TextInput
              style={styles.textInput}
              placeholder="Enter video call link (Zoom, Teams, etc.)"
              value={interviewData.meetingLink}
              onChangeText={(value) => updateInterviewData('meetingLink', value)}
              autoCapitalize="none"
            />
          </View>
        )}
        
        {interviewData.format === 'in_person' && (
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Physical Location</Text>
            <TextInput
              style={styles.textInput}
              placeholder="Enter office address or meeting room"
              value={interviewData.location}
              onChangeText={(value) => updateInterviewData('location', value)}
            />
          </View>
        )}
        
        {interviewData.format === 'phone' && (
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Phone Number</Text>
            <TextInput
              style={styles.textInput}
              placeholder="Enter phone number for the call"
              value={interviewData.location}
              onChangeText={(value) => updateInterviewData('location', value)}
              keyboardType="phone-pad"
            />
          </View>
        )}
        
        {interviewData.format === 'hybrid' && (
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Location Details</Text>
            <TextInput
              style={[styles.textInput, styles.textArea]}
              placeholder="Describe the hybrid interview setup"
              value={interviewData.location}
              onChangeText={(value) => updateInterviewData('location', value)}
              multiline
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>
        )}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Priority Level</Text>
        
        <View style={styles.priorityList}>
          {priorityLevels.map((priority) => (
            <TouchableOpacity
              key={priority.id}
              style={[
                styles.priorityCard,
                interviewData.priority === priority.id && styles.selectedPriorityCard,
              ]}
              onPress={() => updateInterviewData('priority', priority.id)}
            >
              <View style={[styles.priorityIndicator, { backgroundColor: priority.color }]} />
              <View style={styles.priorityInfo}>
                <Text style={[
                  styles.priorityName,
                  interviewData.priority === priority.id && styles.selectedPriorityName,
                ]}>
                  {priority.name}
                </Text>
                <Text style={styles.priorityDescription}>{priority.description}</Text>
              </View>
              {interviewData.priority === priority.id && (
                <MaterialCommunityIcons name="check-circle" size={20} color="#3b82f6" />
              )}
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </ScrollView>
  );

  // Render schedule step
  const renderScheduleStep = () => (
    <ScrollView
      style={styles.stepContent}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Select Date</Text>
        
        <TouchableOpacity
          style={styles.dateSelector}
          onPress={() => setShowCalendar(true)}
        >
          <MaterialCommunityIcons name="calendar" size={24} color="#3b82f6" />
          <Text style={styles.dateSelectorText}>
            {interviewData.date ? new Date(interviewData.date).toLocaleDateString('en-GB', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }) : 'Select Date'}
          </Text>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#6b7280" />
        </TouchableOpacity>
      </View>

      {interviewData.date && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Available Time Slots</Text>
          
          <View style={styles.timeSlotsGrid}>
            {timeSlots.filter(slot => slot.available).map((slot, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.timeSlot,
                  interviewData.time === slot.time && styles.selectedTimeSlot,
                ]}
                onPress={() => updateInterviewData('time', slot.time)}
              >
                <Text style={[
                  styles.timeSlotText,
                  interviewData.time === slot.time && styles.selectedTimeSlotText,
                ]}>
                  {slot.time}
                </Text>
                {slot.interviewer && (
                  <Text style={[
                    styles.timeSlotInterviewer,
                    interviewData.time === slot.time && styles.selectedTimeSlotInterviewer,
                  ]}>
                    {slot.interviewer}
                  </Text>
                )}
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )}

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Select Interviewers</Text>
        
        <TouchableOpacity
          style={styles.interviewerSelector}
          onPress={() => setShowInterviewerSelection(true)}
        >
          <MaterialCommunityIcons name="account-group" size={24} color="#3b82f6" />
          <Text style={styles.interviewerSelectorText}>
            {interviewData.selectedInterviewers.length > 0 
              ? `${interviewData.selectedInterviewers.length} interviewer(s) selected`
              : 'Select Interviewers'
            }
          </Text>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#6b7280" />
        </TouchableOpacity>
        
        {interviewData.selectedInterviewers.length > 0 && (
          <View style={styles.selectedInterviewers}>
            {interviewData.selectedInterviewers.map((interviewerId) => {
              const interviewer = interviewers.find(i => i.id === interviewerId);
              return interviewer ? (
                <View key={interviewer.id} style={styles.selectedInterviewerCard}>
                  <View style={styles.interviewerInfo}>
                    <Text style={styles.interviewerName}>{interviewer.name}</Text>
                    <Text style={styles.interviewerTitle}>{interviewer.title}</Text>
                  </View>
                  <TouchableOpacity
                    style={styles.removeInterviewerButton}
                    onPress={() => {
                      const updated = interviewData.selectedInterviewers.filter(id => id !== interviewerId);
                      updateInterviewData('selectedInterviewers', updated);
                    }}
                  >
                    <MaterialCommunityIcons name="close" size={16} color="#ef4444" />
                  </TouchableOpacity>
                </View>
              ) : null;
            })}
          </View>
        )}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Timezone</Text>
        
        <View style={styles.timezoneSelector}>
          {timezones.map((timezone) => (
            <TouchableOpacity
              key={timezone.id}
              style={[
                styles.timezoneOption,
                interviewData.timezone === timezone.id && styles.selectedTimezoneOption,
              ]}
              onPress={() => updateInterviewData('timezone', timezone.id)}
            >
              <Text style={[
                styles.timezoneText,
                interviewData.timezone === timezone.id && styles.selectedTimezoneText,
              ]}>
                {timezone.name}
              </Text>
              <Text style={[
                styles.timezoneOffset,
                interviewData.timezone === timezone.id && styles.selectedTimezoneOffset,
              ]}>
                {timezone.offset}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </ScrollView>
  );

  // Render settings step
  const renderSettingsStep = () => (
    <ScrollView
      style={styles.stepContent}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Interview Questions</Text>
        
        <View style={styles.questionsContainer}>
          {interviewData.questions.map((question, index) => (
            <View key={index} style={styles.questionItem}>
              <Text style={styles.questionText}>{question}</Text>
              <TouchableOpacity
                style={styles.removeQuestionButton}
                onPress={() => {
                  const updated = interviewData.questions.filter((_, i) => i !== index);
                  updateInterviewData('questions', updated);
                }}
              >
                <MaterialCommunityIcons name="close" size={16} color="#ef4444" />
              </TouchableOpacity>
            </View>
          ))}
          
          <TouchableOpacity
            style={styles.addQuestionButton}
            onPress={() => {
              Alert.prompt(
                'Add Question',
                'Enter interview question:',
                (text) => {
                  if (text && text.trim()) {
                    const updated = [...interviewData.questions, text.trim()];
                    updateInterviewData('questions', updated);
                  }
                }
              );
            }}
          >
            <MaterialCommunityIcons name="plus" size={20} color="#3b82f6" />
            <Text style={styles.addQuestionText}>Add Question</Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Instructions</Text>
        <TextInput
          style={[styles.textInput, styles.textArea]}
          placeholder="Enter instructions for the interviewer..."
          value={interviewData.instructions}
          onChangeText={(value) => updateInterviewData('instructions', value)}
          multiline
          numberOfLines={4}
          textAlignVertical="top"
        />
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Notes</Text>
        <TextInput
          style={[styles.textInput, styles.textArea]}
          placeholder="Enter additional notes..."
          value={interviewData.notes}
          onChangeText={(value) => updateInterviewData('notes', value)}
          multiline
          numberOfLines={4}
          textAlignVertical="top"
        />
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Interview Settings</Text>
        
        <View style={styles.settingsList}>
          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingTitle}>Send Reminder</Text>
              <Text style={styles.settingDescription}>Send email reminder to participants</Text>
            </View>
            <Switch
              value={interviewData.reminderEnabled}
              onValueChange={(value) => updateInterviewData('reminderEnabled', value)}
              trackColor={{ false: '#d1d5db', true: '#3b82f6' }}
              thumbColor={interviewData.reminderEnabled ? '#ffffff' : '#f4f3f4'}
            />
          </View>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingTitle}>Enable Recording</Text>
              <Text style={styles.settingDescription}>Record the interview session</Text>
            </View>
            <Switch
              value={interviewData.recordingEnabled}
              onValueChange={(value) => updateInterviewData('recordingEnabled', value)}
              trackColor={{ false: '#d1d5db', true: '#3b82f6' }}
              thumbColor={interviewData.recordingEnabled ? '#ffffff' : '#f4f3f4'}
            />
          </View>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingTitle}>Auto Transcription</Text>
              <Text style={styles.settingDescription}>Generate automatic transcript</Text>
            </View>
            <Switch
              value={interviewData.transcriptionEnabled}
              onValueChange={(value) => updateInterviewData('transcriptionEnabled', value)}
              trackColor={{ false: '#d1d5db', true: '#3b82f6' }}
              thumbColor={interviewData.transcriptionEnabled ? '#ffffff' : '#f4f3f4'}
            />
          </View>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingTitle}>AI Analysis</Text>
              <Text style={styles.settingDescription}>Enable AI-powered interview analysis</Text>
            </View>
            <Switch
              value={interviewData.aiAnalysisEnabled}
              onValueChange={(value) => updateInterviewData('aiAnalysisEnabled', value)}
              trackColor={{ false: '#d1d5db', true: '#3b82f6' }}
              thumbColor={interviewData.aiAnalysisEnabled ? '#ffffff' : '#f4f3f4'}
            />
          </View>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingTitle}>Send Calendar Invite</Text>
              <Text style={styles.settingDescription}>Send calendar invitation to all participants</Text>
            </View>
            <Switch
              value={interviewData.sendCalendarInvite}
              onValueChange={(value) => updateInterviewData('sendCalendarInvite', value)}
              trackColor={{ false: '#d1d5db', true: '#3b82f6' }}
              thumbColor={interviewData.sendCalendarInvite ? '#ffffff' : '#f4f3f4'}
            />
          </View>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingTitle}>Notify Team</Text>
              <Text style={styles.settingDescription}>Notify team members about the interview</Text>
            </View>
            <Switch
              value={interviewData.notifyTeam}
              onValueChange={(value) => updateInterviewData('notifyTeam', value)}
              trackColor={{ false: '#d1d5db', true: '#3b82f6' }}
              thumbColor={interviewData.notifyTeam ? '#ffffff' : '#f4f3f4'}
            />
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Advanced Settings</Text>
        
        <View style={styles.advancedSettings}>
          <View style={styles.settingRow}>
            <Text style={styles.settingLabel}>Preparation Time (minutes)</Text>
            <View style={styles.numberSelector}>
              {[0, 15, 30, 45].map((time) => (
                <TouchableOpacity
                  key={time}
                  style={[
                    styles.numberOption,
                    interviewData.preparationTime === time && styles.selectedNumberOption,
                  ]}
                  onPress={() => updateInterviewData('preparationTime', time)}
                >
                  <Text style={[
                    styles.numberOptionText,
                    interviewData.preparationTime === time && styles.selectedNumberOptionText,
                  ]}>
                    {time}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <View style={styles.settingRow}>
            <Text style={styles.settingLabel}>Buffer Time (minutes)</Text>
            <View style={styles.numberSelector}>
              {[0, 15, 30, 45].map((time) => (
                <TouchableOpacity
                  key={time}
                  style={[
                    styles.numberOption,
                    interviewData.bufferTime === time && styles.selectedNumberOption,
                  ]}
                  onPress={() => updateInterviewData('bufferTime', time)}
                >
                  <Text style={[
                    styles.numberOptionText,
                    interviewData.bufferTime === time && styles.selectedNumberOptionText,
                  ]}>
                    {time}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <View style={styles.settingRow}>
            <Text style={styles.settingLabel}>Max Reschedules</Text>
            <View style={styles.numberSelector}>
              {[0, 1, 2, 3].map((count) => (
                <TouchableOpacity
                  key={count}
                  style={[
                    styles.numberOption,
                    interviewData.maxReschedules === count && styles.selectedNumberOption,
                  ]}
                  onPress={() => updateInterviewData('maxReschedules', count)}
                >
                  <Text style={[
                    styles.numberOptionText,
                    interviewData.maxReschedules === count && styles.selectedNumberOptionText,
                  ]}>
                    {count}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
      </View>
    </ScrollView>
  );

  // Render review step
  const renderReviewStep = () => (
    <ScrollView
      style={styles.stepContent}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Interview Summary</Text>
        
        <View style={styles.summaryCard}>
          <View style={styles.summaryHeader}>
            <Text style={styles.summaryTitle}>{interviewData.title}</Text>
            <View style={[styles.summaryTypeTag, {
              backgroundColor: interviewTypes.find(t => t.id === interviewData.type)?.color || '#3b82f6'
            }]}>
              <Text style={styles.summaryTypeText}>
                {interviewTypes.find(t => t.id === interviewData.type)?.name || interviewData.type}
              </Text>
            </View>
          </View>
          
          <View style={styles.summaryDetails}>
            <View style={styles.summaryDetailItem}>
              <MaterialCommunityIcons name="calendar" size={16} color="#6b7280" />
              <Text style={styles.summaryDetailText}>
                {interviewData.date ? new Date(interviewData.date).toLocaleDateString('en-GB') : 'Not selected'}
              </Text>
            </View>
            
            <View style={styles.summaryDetailItem}>
              <MaterialCommunityIcons name="clock" size={16} color="#6b7280" />
              <Text style={styles.summaryDetailText}>
                {interviewData.time || 'Not selected'} ({interviewData.duration} minutes)
              </Text>
            </View>
            
            <View style={styles.summaryDetailItem}>
              <MaterialCommunityIcons name="earth" size={16} color="#6b7280" />
              <Text style={styles.summaryDetailText}>{interviewData.timezone}</Text>
            </View>
            
            <View style={styles.summaryDetailItem}>
              <MaterialCommunityIcons 
                name={interviewFormats.find(f => f.id === interviewData.format)?.icon || 'video'} 
                size={16} 
                color="#6b7280" 
              />
              <Text style={styles.summaryDetailText}>
                {interviewFormats.find(f => f.id === interviewData.format)?.name || interviewData.format}
              </Text>
            </View>
            
            {interviewData.location && (
              <View style={styles.summaryDetailItem}>
                <MaterialCommunityIcons name="map-marker" size={16} color="#6b7280" />
                <Text style={styles.summaryDetailText}>{interviewData.location}</Text>
              </View>
            )}
            
            {interviewData.meetingLink && (
              <View style={styles.summaryDetailItem}>
                <MaterialCommunityIcons name="link" size={16} color="#6b7280" />
                <Text style={styles.summaryDetailText}>Meeting link provided</Text>
              </View>
            )}
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Participants</Text>
        
        {candidate && (
          <View style={styles.participantCard}>
            <View style={styles.participantHeader}>
              <MaterialCommunityIcons name="account" size={24} color="#3b82f6" />
              <Text style={styles.participantRole}>Candidate</Text>
            </View>
            <Text style={styles.participantName}>{candidate.name}</Text>
            <Text style={styles.participantEmail}>{candidate.email}</Text>
          </View>
        )}
        
        {interviewData.selectedInterviewers.map((interviewerId) => {
          const interviewer = interviewers.find(i => i.id === interviewerId);
          return interviewer ? (
            <View key={interviewer.id} style={styles.participantCard}>
              <View style={styles.participantHeader}>
                <MaterialCommunityIcons name="account-tie" size={24} color="#10b981" />
                <Text style={styles.participantRole}>Interviewer</Text>
              </View>
              <Text style={styles.participantName}>{interviewer.name}</Text>
              <Text style={styles.participantTitle}>{interviewer.title}</Text>
              <Text style={styles.participantEmail}>{interviewer.email}</Text>
            </View>
          ) : null;
        })}
      </View>

      {job && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Position Details</Text>
          
          <View style={styles.jobSummaryCard}>
            <Text style={styles.jobSummaryTitle}>{job.title}</Text>
            <Text style={styles.jobSummaryDepartment}>{job.department}</Text>
            <Text style={styles.jobSummaryLocation}>{job.location}</Text>
            <Text style={styles.jobSummarySalary}>{job.salary}</Text>
          </View>
        </View>
      )}

      {interviewData.questions.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Interview Questions</Text>
          
          <View style={styles.questionsList}>
            {interviewData.questions.map((question, index) => (
              <View key={index} style={styles.questionSummaryItem}>
                <Text style={styles.questionNumber}>{index + 1}.</Text>
                <Text style={styles.questionSummaryText}>{question}</Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {(interviewData.instructions || interviewData.notes) && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Additional Information</Text>
          
          {interviewData.instructions && (
            <View style={styles.additionalInfoCard}>
              <Text style={styles.additionalInfoLabel}>Instructions:</Text>
              <Text style={styles.additionalInfoText}>{interviewData.instructions}</Text>
            </View>
          )}
          
          {interviewData.notes && (
            <View style={styles.additionalInfoCard}>
              <Text style={styles.additionalInfoLabel}>Notes:</Text>
              <Text style={styles.additionalInfoText}>{interviewData.notes}</Text>
            </View>
          )}
        </View>
      )}

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Settings Summary</Text>
        
        <View style={styles.settingsSummary}>
          <View style={styles.settingSummaryItem}>
            <MaterialCommunityIcons 
              name={interviewData.reminderEnabled ? "check-circle" : "close-circle"} 
              size={20} 
              color={interviewData.reminderEnabled ? "#10b981" : "#ef4444"} 
            />
            <Text style={styles.settingSummaryText}>Email Reminders</Text>
          </View>
          
          <View style={styles.settingSummaryItem}>
            <MaterialCommunityIcons 
              name={interviewData.recordingEnabled ? "check-circle" : "close-circle"} 
              size={20} 
              color={interviewData.recordingEnabled ? "#10b981" : "#ef4444"} 
            />
            <Text style={styles.settingSummaryText}>Recording Enabled</Text>
          </View>
          
          <View style={styles.settingSummaryItem}>
            <MaterialCommunityIcons 
              name={interviewData.sendCalendarInvite ? "check-circle" : "close-circle"} 
              size={20} 
              color={interviewData.sendCalendarInvite ? "#10b981" : "#ef4444"} 
            />
            <Text style={styles.settingSummaryText}>Calendar Invites</Text>
          </View>
          
          <View style={styles.settingSummaryItem}>
            <MaterialCommunityIcons 
              name={interviewData.notifyTeam ? "check-circle" : "close-circle"} 
              size={20} 
              color={interviewData.notifyTeam ? "#10b981" : "#ef4444"} 
            />
            <Text style={styles.settingSummaryText}>Team Notifications</Text>
          </View>
        </View>
      </View>
    </ScrollView>
  );

  // ──────────────────────────────────────────────────────────────────────
  // Candidate picker modal — shows the real pipeline so the recruiter can
  // pick someone to schedule an interview with. Filters by name/email/job
  // as they type. Each row is a real applications.id; tapping it threads
  // the candidate through to the rest of the form.
  // ──────────────────────────────────────────────────────────────────────
  const renderCandidatePickerModal = () => {
    const q = (candidateSearch || '').toLowerCase().trim();
    const filtered = !q ? availableCandidates : availableCandidates.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.jobTitle || '').toLowerCase().includes(q)
    );
    return (
      <Modal
        visible={showCandidateModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowCandidateModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity style={styles.modalCloseButton} onPress={() => setShowCandidateModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6b7280" />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Select Candidate</Text>
            <TouchableOpacity style={styles.modalCloseButton} onPress={loadAvailableCandidates}>
              <MaterialCommunityIcons name="refresh" size={22} color="#6b7280" />
            </TouchableOpacity>
          </View>

          <View style={{ paddingHorizontal: 16, paddingVertical: 12 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#f1f5f9', borderRadius: 10, paddingHorizontal: 12 }}>
              <MaterialCommunityIcons name="magnify" size={20} color="#94a3b8" />
              <TextInput
                style={{ flex: 1, paddingVertical: 10, paddingHorizontal: 8, fontSize: 15, color: '#0f172a' }}
                placeholder="Search by name, email, or job"
                placeholderTextColor="#94a3b8"
                value={candidateSearch}
                onChangeText={setCandidateSearch}
              />
              {candidateSearch.length > 0 && (
                <TouchableOpacity onPress={() => setCandidateSearch('')}>
                  <MaterialCommunityIcons name="close-circle" size={18} color="#94a3b8" />
                </TouchableOpacity>
              )}
            </View>
          </View>

          {loadingCandidates ? (
            <View style={{ paddingVertical: 60, alignItems: 'center' }}>
              <ActivityIndicator size="large" color="#3b82f6" />
              <Text style={{ marginTop: 12, color: '#64748b' }}>Loading candidates...</Text>
            </View>
          ) : filtered.length === 0 ? (
            <View style={{ paddingVertical: 60, paddingHorizontal: 24, alignItems: 'center' }}>
              <MaterialCommunityIcons name="account-search" size={48} color="#cbd5e1" />
              <Text style={{ marginTop: 12, color: '#64748b', fontSize: 15, textAlign: 'center' }}>
                {availableCandidates.length === 0
                  ? 'No candidates in your pipeline yet. Post a job and wait for applications.'
                  : `No candidates match "${candidateSearch}".`}
              </Text>
            </View>
          ) : (
            <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
              {filtered.map((c) => (
                <TouchableOpacity
                  key={String(c.id)}
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: 12,
                    padding: 14,
                    marginBottom: 10,
                    flexDirection: 'row',
                    alignItems: 'center',
                    borderWidth: 1,
                    borderColor: '#e5e7eb',
                  }}
                  onPress={() => handleCandidatePick(c)}
                >
                  <View style={{
                    width: 44, height: 44, borderRadius: 22,
                    backgroundColor: '#dbeafe',
                    alignItems: 'center', justifyContent: 'center',
                    marginRight: 12,
                  }}>
                    {c.avatar ? (
                      <Image source={{ uri: c.avatar }} style={{ width: 44, height: 44, borderRadius: 22 }} />
                    ) : (
                      <Text style={{ color: '#1d4ed8', fontWeight: '700' }}>
                        {(c.name || 'A').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()}
                      </Text>
                    )}
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 15, fontWeight: '600', color: '#0f172a' }} numberOfLines={1}>{c.name}</Text>
                    <Text style={{ fontSize: 13, color: '#64748b', marginTop: 2 }} numberOfLines={1}>
                      {c.jobTitle || c.currentTitle || c.email || '—'}
                    </Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
                      <View style={{
                        backgroundColor: '#f1f5f9',
                        paddingHorizontal: 8, paddingVertical: 2,
                        borderRadius: 6, marginRight: 6,
                      }}>
                        <Text style={{ fontSize: 11, color: '#475569', fontWeight: '500', textTransform: 'capitalize' }}>{c.stage}</Text>
                      </View>
                      {c.interviewsScheduled > 0 && (
                        <Text style={{ fontSize: 11, color: '#64748b' }}>
                          {c.interviewsScheduled} interview{c.interviewsScheduled === 1 ? '' : 's'} scheduled
                        </Text>
                      )}
                    </View>
                  </View>
                  <MaterialCommunityIcons name="chevron-right" size={20} color="#94a3b8" />
                </TouchableOpacity>
              ))}
            </ScrollView>
          )}
        </SafeAreaView>
      </Modal>
    );
  };

  // Date picker modal — a clean month-grid calendar. Replaces the previous
  // broken "Select Date" button that triggered showCalendar=true but had no
  // matching Modal anywhere, so the button silently did nothing.
  // Lets the recruiter navigate months back/forward, disables past dates and
  // weekends (visually de-emphasised), and writes the ISO date string into
  // interviewData.date so the time-slot section unlocks.
  const renderDatePickerModal = () => {
    // Track which month the user is viewing — defaults to current month, or
    // to the month of the already-selected date if one exists.
    const todayISO = new Date().toISOString().slice(0, 10);
    const baseDate = interviewData.date ? new Date(interviewData.date) : new Date();
    const viewYear  = calMonth.getFullYear();
    const viewMonth = calMonth.getMonth();

    // Build the grid: leading blanks for the first-of-month's weekday,
    // then 1..N for that month's days.
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const daysInMonth  = new Date(viewYear, viewMonth + 1, 0).getDate();
    // Make Monday the first column (UK convention)
    const firstWeekday = (firstOfMonth.getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    // Pad to complete the last row
    while (cells.length % 7 !== 0) cells.push(null);

    const monthLabel = firstOfMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

    const pickDate = (day) => {
      const picked = new Date(viewYear, viewMonth, day);
      // Local-tz YYYY-MM-DD so we don't shift the day across timezones
      const iso = `${picked.getFullYear()}-${String(picked.getMonth() + 1).padStart(2, '0')}-${String(picked.getDate()).padStart(2, '0')}`;
      updateInterviewData('date', iso);
      setShowCalendar(false);
    };

    return (
      <Modal
        visible={showCalendar}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowCalendar(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity style={styles.modalCloseButton} onPress={() => setShowCalendar(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6b7280" />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Pick Interview Date</Text>
            <View style={{ width: 40 }} />
          </View>

          {/* Month nav */}
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12 }}>
            <TouchableOpacity
              onPress={() => setCalMonth(new Date(viewYear, viewMonth - 1, 1))}
              style={{ padding: 8 }}
            >
              <MaterialCommunityIcons name="chevron-left" size={26} color="#3b82f6" />
            </TouchableOpacity>
            <Text style={{ fontSize: 18, fontWeight: '700', color: '#0f172a' }}>{monthLabel}</Text>
            <TouchableOpacity
              onPress={() => setCalMonth(new Date(viewYear, viewMonth + 1, 1))}
              style={{ padding: 8 }}
            >
              <MaterialCommunityIcons name="chevron-right" size={26} color="#3b82f6" />
            </TouchableOpacity>
          </View>

          {/* Weekday header */}
          <View style={{ flexDirection: 'row', paddingHorizontal: 12 }}>
            {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
              <Text key={d} style={{ flex: 1, textAlign: 'center', fontSize: 12, color: '#64748b', fontWeight: '600' }}>{d}</Text>
            ))}
          </View>

          {/* Day grid */}
          <View style={{ paddingHorizontal: 12, paddingTop: 8 }}>
            {Array.from({ length: cells.length / 7 }, (_, rowIdx) => (
              <View key={rowIdx} style={{ flexDirection: 'row', marginBottom: 6 }}>
                {cells.slice(rowIdx * 7, rowIdx * 7 + 7).map((day, colIdx) => {
                  if (day == null) {
                    return <View key={colIdx} style={{ flex: 1, aspectRatio: 1 }} />;
                  }
                  const cellDate = new Date(viewYear, viewMonth, day);
                  const cellISO = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const isPast = cellISO < todayISO;
                  const isSelected = interviewData.date === cellISO;
                  const isToday = cellISO === todayISO;
                  const isWeekend = cellDate.getDay() === 0 || cellDate.getDay() === 6;
                  return (
                    <TouchableOpacity
                      key={colIdx}
                      disabled={isPast}
                      onPress={() => pickDate(day)}
                      style={{
                        flex: 1, aspectRatio: 1, margin: 2, borderRadius: 10,
                        alignItems: 'center', justifyContent: 'center',
                        backgroundColor: isSelected ? '#3b82f6'
                                       : isToday    ? '#dbeafe'
                                       : 'transparent',
                        borderWidth: isToday && !isSelected ? 1 : 0,
                        borderColor: '#3b82f6',
                        opacity: isPast ? 0.3 : 1,
                      }}
                    >
                      <Text style={{
                        fontSize: 15,
                        fontWeight: isSelected ? '700' : '500',
                        color: isSelected ? '#fff'
                             : isPast     ? '#cbd5e1'
                             : isWeekend  ? '#94a3b8'
                             : '#0f172a',
                      }}>
                        {day}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            ))}
          </View>

          {/* Quick picks */}
          <View style={{ paddingHorizontal: 16, paddingVertical: 16, borderTopWidth: 1, borderTopColor: '#e5e7eb', marginTop: 12 }}>
            <Text style={{ fontSize: 12, color: '#64748b', fontWeight: '600', marginBottom: 8 }}>QUICK PICKS</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              {[
                { label: 'Tomorrow', offset: 1 },
                { label: 'In 3 days', offset: 3 },
                { label: 'Next week', offset: 7 },
                { label: 'In 2 weeks', offset: 14 },
              ].map(q => {
                const d = new Date();
                d.setDate(d.getDate() + q.offset);
                const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                return (
                  <TouchableOpacity
                    key={q.label}
                    onPress={() => {
                      updateInterviewData('date', iso);
                      setCalMonth(new Date(d.getFullYear(), d.getMonth(), 1));
                      setShowCalendar(false);
                    }}
                    style={{
                      paddingHorizontal: 12, paddingVertical: 8, borderRadius: 16,
                      backgroundColor: '#eff6ff', borderWidth: 1, borderColor: '#bfdbfe',
                    }}
                  >
                    <Text style={{ color: '#1e40af', fontSize: 13, fontWeight: '600' }}>{q.label}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        </SafeAreaView>
      </Modal>
    );
  };

  // Render template modal
  const renderTemplateModal = () => (
    <Modal
      visible={showTemplateModal}
      animationType="slide"
      presentationStyle="pageSheet"
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => setShowTemplateModal(false)}
          >
            <MaterialCommunityIcons name="close" size={24} color="#6b7280" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Choose Template</Text>
          <View style={styles.modalSaveButton} />
        </View>

        <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
          {Object.entries({
            technical: {
              title: 'Technical Interview',
              description: 'Comprehensive technical assessment with coding challenges',
              duration: 90,
              questions: 4,
              color: '#10b981',
            },
            behavioral: {
              title: 'Behavioral Interview',
              description: 'Cultural fit and soft skills evaluation',
              duration: 60,
              questions: 4,
              color: '#8b5cf6',
            },
            final: {
              title: 'Final Interview',
              description: 'Final round with leadership and compensation discussion',
              duration: 60,
              questions: 4,
              color: '#ef4444',
            },
          }).map(([key, template]) => (
            <TouchableOpacity
              key={key}
              style={styles.templateCard}
              onPress={() => loadTemplate(key)}
            >
              <View style={styles.templateHeader}>
                <Text style={styles.templateTitle}>{template.title}</Text>
                <View style={[styles.templateTypeTag, { backgroundColor: template.color }]}>
                  <Text style={styles.templateTypeText}>Template</Text>
                </View>
              </View>
              <Text style={styles.templateDescription}>{template.description}</Text>
              <View style={styles.templateMeta}>
                <Text style={styles.templateMetaText}>{template.duration} minutes</Text>
                <Text style={styles.templateMetaText}>•</Text>
                <Text style={styles.templateMetaText}>{template.questions} questions</Text>
              </View>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // Render interviewer selection modal
  const renderInterviewerSelectionModal = () => (
    <Modal
      visible={showInterviewerSelection}
      animationType="slide"
      presentationStyle="pageSheet"
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => setShowInterviewerSelection(false)}
          >
            <MaterialCommunityIcons name="close" size={24} color="#6b7280" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Select Interviewers</Text>
          <TouchableOpacity
            style={styles.modalSaveButton}
            onPress={() => setShowInterviewerSelection(false)}
          >
            <Text style={styles.modalSaveText}>Done</Text>
          </TouchableOpacity>
        </View>

        <FlatList
          data={interviewers}
          keyExtractor={(item) => item.id}
          style={styles.modalContent}
          showsVerticalScrollIndicator={false}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={[
                styles.interviewerCard,
                interviewData.selectedInterviewers.includes(item.id) && styles.selectedInterviewerCard,
              ]}
              onPress={() => {
                const isSelected = interviewData.selectedInterviewers.includes(item.id);
                let updated;
                if (isSelected) {
                  updated = interviewData.selectedInterviewers.filter(id => id !== item.id);
                } else {
                  updated = [...interviewData.selectedInterviewers, item.id];
                }
                updateInterviewData('selectedInterviewers', updated);
              }}
            >
              <View style={styles.interviewerCardHeader}>
                <View style={styles.interviewerCardInfo}>
                  <Text style={styles.interviewerCardName}>{item.name || 'Team member'}</Text>
                  {/* Real employees have job_title (mapped to .title or .role) */}
                  {(item.title || item.role) ? (
                    <Text style={styles.interviewerCardTitle}>{item.title || item.role}</Text>
                  ) : null}
                  {item.department ? (
                    <Text style={styles.interviewerCardDepartment}>{item.department}</Text>
                  ) : null}
                </View>
                {interviewData.selectedInterviewers.includes(item.id) && (
                  <MaterialCommunityIcons name="check-circle" size={24} color="#3b82f6" />
                )}
              </View>

              <View style={styles.interviewerCardDetails}>
                {/* Only show rating block if it exists — real employees won't have it */}
                {item.rating ? (
                  <View style={styles.interviewerCardDetailItem}>
                    <MaterialCommunityIcons name="star" size={16} color="#f59e0b" />
                    <Text style={styles.interviewerCardDetailText}>
                      {item.rating}{item.totalInterviews ? ` (${item.totalInterviews} interviews)` : ''}
                    </Text>
                  </View>
                ) : null}
                {item.email ? (
                  <View style={styles.interviewerCardDetailItem}>
                    <MaterialCommunityIcons name="email" size={16} color="#6b7280" />
                    <Text style={styles.interviewerCardDetailText}>{item.email}</Text>
                  </View>
                ) : null}
              </View>

              {/* Expertise — defensive: only render when actually present */}
              {Array.isArray(item.expertise) && item.expertise.length > 0 ? (
                <View style={styles.interviewerExpertise}>
                  <Text style={styles.expertiseLabel}>Expertise:</Text>
                  <Text style={styles.expertiseText}>{item.expertise.join(', ')}</Text>
                </View>
              ) : null}
            </TouchableOpacity>
          )}
        />
      </SafeAreaView>
    </Modal>
  );

  // Render navigation buttons
  const renderNavigationButtons = () => (
    <View style={styles.navigationButtons}>
      {currentStep > 1 && (
        <TouchableOpacity
          style={styles.backButton}
          onPress={previousStep}
        >
          <MaterialCommunityIcons name="chevron-left" size={20} color="#6b7280" />
          <Text style={styles.backButtonText}>Back</Text>
        </TouchableOpacity>
      )}
      
      <View style={styles.navigationSpacer} />
      
      {currentStep < totalSteps ? (
        <TouchableOpacity
          style={[styles.nextButton, !validateStep() && styles.disabledButton]}
          onPress={nextStep}
          disabled={!validateStep()}
        >
          <Text style={styles.nextButtonText}>Next</Text>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#ffffff" />
        </TouchableOpacity>
      ) : (
        <TouchableOpacity
          style={[styles.saveButton, isSaving && styles.savingButton]}
          onPress={saveInterview}
          disabled={isSaving}
        >
          {isSaving ? (
            <ActivityIndicator size={20} color="#ffffff" />
          ) : (
            <>
              <MaterialCommunityIcons name="check" size={20} color="#ffffff" />
              <Text style={styles.saveButtonText}>
                <Text>{editingInterview ? 'Update Interview' : 'Schedule Interview'}</Text>
              </Text>
            </>
          )}
        </TouchableOpacity>
      )}
    </View>
  );

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderTypeStep();
      case 2:
        return renderDetailsStep();
      case 3:
        return renderScheduleStep();
      case 4:
        return renderSettingsStep();
      case 5:
        return renderReviewStep();
      default:
        return renderTypeStep();
    }
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#3b82f6" />
          <Text style={styles.loadingText}>Loading interview data...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#1e293b" />
      
      {/* Header */}
      <LinearGradient
        colors={['#1e293b', '#334155']}
        style={styles.header}
      >
        <TouchableOpacity
          style={styles.headerBackButton}
          onPress={() => navigation && navigation.goBack ? navigation.goBack() : null}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
        </TouchableOpacity>

        {/* Company logo (left) — falls back to a placeholder avatar when
            the company hasn't uploaded one yet. */}
        {_companyLogo ? (
          <Image
            source={{ uri: _companyLogo }}
            style={{ width: 38, height: 38, borderRadius: 8, marginRight: 10, backgroundColor: '#FFF' }}
            resizeMode="contain"
          />
        ) : (
          <View style={{ width: 38, height: 38, borderRadius: 8, marginRight: 10, backgroundColor: 'rgba(255,255,255,0.12)', justifyContent: 'center', alignItems: 'center' }}>
            <MaterialCommunityIcons name="office-building" size={20} color="#ffffff" />
          </View>
        )}

        {/* Company name on top, screen title underneath. Falls back to just
            the screen title when no company name is loaded. */}
        <View style={{ flex: 1 }}>
          {_companyName ? (
            <Text style={{ color: '#FFF', fontSize: 13, fontWeight: '600' }} numberOfLines={1}>
              {_companyName}
            </Text>
          ) : null}
          <Text style={[styles.headerTitle, { textAlign: 'left', marginLeft: 0 }]}>
            {editingInterview ? 'Edit Interview' : 'Schedule Interview'}
          </Text>
        </View>
        <View style={styles.headerSpacer} />
      </LinearGradient>

      {/* Company details banner — chips for industry / location / team size /
          website. Only shows chips for fields that actually have a value, so
          a partially-filled company profile stays clean. */}
      {(_companyProfile && (_companyProfile.industry || _companyProfile.headquarters || _companyProfile.city || _companyProfile.employee_count)) ? (
        <View style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: 6,
          paddingHorizontal: 16,
          paddingVertical: 10,
          backgroundColor: '#0e4e96',
          borderBottomWidth: 1,
          borderBottomColor: 'rgba(255,255,255,0.1)',
        }}>
          {_companyProfile.industry ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.18)', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 }}>
              <MaterialCommunityIcons name="briefcase" size={11} color="#FFF" />
              <Text style={{ color: '#FFF', fontSize: 11, marginLeft: 4 }}>{_companyProfile.industry}</Text>
            </View>
          ) : null}
          {(_companyProfile.headquarters || _companyProfile.city) ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.18)', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 }}>
              <MaterialCommunityIcons name="map-marker" size={11} color="#FFF" />
              <Text style={{ color: '#FFF', fontSize: 11, marginLeft: 4 }}>
                {_companyProfile.headquarters || _companyProfile.city}
              </Text>
            </View>
          ) : null}
          {_companyProfile.employee_count ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.18)', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 }}>
              <MaterialCommunityIcons name="account-group" size={11} color="#FFF" />
              <Text style={{ color: '#FFF', fontSize: 11, marginLeft: 4 }}>{_companyProfile.employee_count} team</Text>
            </View>
          ) : null}
          {_companyProfile.website ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.18)', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 }}>
              <MaterialCommunityIcons name="web" size={11} color="#FFF" />
              <Text style={{ color: '#FFF', fontSize: 11, marginLeft: 4 }} numberOfLines={1}>
                {_companyProfile.website.replace(/^https?:\/\//, '')}
              </Text>
            </View>
          ) : null}
        </View>
      ) : null}

      {/* Step Indicator */}
      {renderStepIndicator()}

      {/* Content */}
      <KeyboardAvoidingView
        style={styles.content}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        {renderStepContent()}
      </KeyboardAvoidingView>

      {/* Navigation */}
      {renderNavigationButtons()}

      {/* Modals */}
      {renderCandidatePickerModal()}
      {renderDatePickerModal()}
      {renderTemplateModal()}
      {renderInterviewerSelectionModal()}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6b7280',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  headerBackButton: {
    padding: 8,
    marginRight: 12,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  headerSpacer: {
    flex: 1,
  },
  stepIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  stepContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#e5e7eb',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  activeStepCircle: {
    backgroundColor: '#3b82f6',
  },
  completedStepCircle: {
    backgroundColor: '#10b981',
  },
  stepNumber: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#6b7280',
  },
  activeStepNumber: {
    color: '#ffffff',
  },
  stepLabel: {
    fontSize: 14,
    color: '#6b7280',
    fontWeight: '500',
  },
  activeStepLabel: {
    color: '#3b82f6',
    fontWeight: '600',
  },
  stepConnector: {
    width: 40,
    height: 2,
    backgroundColor: '#e5e7eb',
    marginHorizontal: 8,
  },
  activeStepConnector: {
    backgroundColor: '#10b981',
  },
  content: {
    flex: 1,
  },
  stepContent: {
    flex: 1,
    padding: 20,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 8,
  },
  sectionDescription: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 16,
    lineHeight: 20,
  },
  typeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  typeCard: {
    width: (width - 60) / 2,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 2,
    borderColor: '#e5e7eb',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  selectedTypeCard: {
    backgroundColor: '#3b82f6',
  },
  typeTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1f2937',
    textAlign: 'center',
    marginTop: 8,
  },
  selectedTypeTitle: {
    color: '#ffffff',
  },
  typeDuration: {
    fontSize: 12,
    color: '#6b7280',
    textAlign: 'center',
    marginTop: 4,
  },
  selectedTypeDuration: {
    color: '#e5e7eb',
  },
  formatList: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  formatCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    marginBottom: 4,
  },
  selectedFormatCard: {
    backgroundColor: '#f0f9ff',
  },
  formatInfo: {
    flex: 1,
    marginLeft: 12,
  },
  formatName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1f2937',
  },
  selectedFormatName: {
    color: '#3b82f6',
  },
  formatDescription: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 2,
  },
  textInput: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#1f2937',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  durationSelector: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  durationOption: {
    flex: 1,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingVertical: 12,
    marginHorizontal: 4,
    alignItems: 'center',
  },
  selectedDurationOption: {
    backgroundColor: '#3b82f6',
    borderColor: '#3b82f6',
  },
  durationText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6b7280',
  },
  selectedDurationText: {
    color: '#ffffff',
  },
  templateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  templateButtonText: {
    flex: 1,
    marginLeft: 12,
    fontSize: 16,
    color: '#3b82f6',
    fontWeight: '500',
  },
  candidateCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  candidateHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  candidateInfo: {
    flex: 1,
  },
  candidateName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  candidatePosition: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 2,
  },
  candidateLocation: {
    fontSize: 12,
    color: '#9ca3af',
    marginTop: 2,
  },
  editButton: {
    padding: 8,
  },
  candidateDetails: {
    marginTop: 8,
  },
  candidateDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  candidateDetailText: {
    marginLeft: 8,
    fontSize: 13,
    color: '#6b7280',
  },
  selectCandidateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    borderWidth: 2,
    borderColor: '#3b82f6',
    borderStyle: 'dashed',
    borderRadius: 12,
    paddingVertical: 20,
  },
  selectCandidateText: {
    marginLeft: 12,
    fontSize: 16,
    color: '#3b82f6',
    fontWeight: '600',
  },
  jobCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  jobHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  jobInfo: {
    flex: 1,
  },
  jobTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  jobDepartment: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 2,
  },
  jobLocation: {
    fontSize: 12,
    color: '#9ca3af',
    marginTop: 2,
  },
  jobDetails: {
    marginTop: 8,
  },
  jobDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  jobDetailText: {
    marginLeft: 8,
    fontSize: 13,
    color: '#6b7280',
  },
  selectJobButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    borderWidth: 2,
    borderColor: '#3b82f6',
    borderStyle: 'dashed',
    borderRadius: 12,
    paddingVertical: 20,
  },
  selectJobText: {
    marginLeft: 12,
    fontSize: 16,
    color: '#3b82f6',
    fontWeight: '600',
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  priorityList: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  priorityCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    marginBottom: 4,
  },
  selectedPriorityCard: {
    backgroundColor: '#f0f9ff',
  },
  priorityIndicator: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
  },
  priorityInfo: {
    flex: 1,
  },
  priorityName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1f2937',
  },
  selectedPriorityName: {
    color: '#3b82f6',
  },
  priorityDescription: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 2,
  },
  dateSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  dateSelectorText: {
    flex: 1,
    marginLeft: 12,
    fontSize: 16,
    color: '#1f2937',
    fontWeight: '500',
  },
  timeSlotsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  timeSlot: {
    width: (width - 60) / 3,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    alignItems: 'center',
  },
  selectedTimeSlot: {
    backgroundColor: '#3b82f6',
    borderColor: '#3b82f6',
  },
  timeSlotText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1f2937',
  },
  selectedTimeSlotText: {
    color: '#ffffff',
  },
  timeSlotInterviewer: {
    fontSize: 10,
    color: '#6b7280',
    marginTop: 2,
  },
  selectedTimeSlotInterviewer: {
    color: '#e5e7eb',
  },
  interviewerSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  interviewerSelectorText: {
    flex: 1,
    marginLeft: 12,
    fontSize: 16,
    color: '#1f2937',
    fontWeight: '500',
  },
  selectedInterviewers: {
    marginTop: 12,
  },
  selectedInterviewerCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f0f9ff',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  interviewerInfo: {
    flex: 1,
  },
  interviewerName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1f2937',
  },
  interviewerTitle: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
  },
  removeInterviewerButton: {
    padding: 4,
  },
  timezoneSelector: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  timezoneOption: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    padding: 12,
    marginRight: 8,
    marginBottom: 8,
    minWidth: 120,
  },
  selectedTimezoneOption: {
    backgroundColor: '#3b82f6',
    borderColor: '#3b82f6',
  },
  timezoneText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1f2937',
  },
  selectedTimezoneText: {
    color: '#ffffff',
  },
  timezoneOffset: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
  },
  selectedTimezoneOffset: {
    color: '#e5e7eb',
  },
  questionsContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  questionItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  questionText: {
    flex: 1,
    fontSize: 14,
    color: '#1f2937',
    lineHeight: 20,
  },
  removeQuestionButton: {
    padding: 4,
    marginLeft: 8,
  },
  addQuestionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    marginTop: 8,
  },
  addQuestionText: {
    marginLeft: 8,
    fontSize: 14,
    color: '#3b82f6',
    fontWeight: '600',
  },
  settingsList: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  settingInfo: {
    flex: 1,
  },
  settingTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1f2937',
  },
  settingDescription: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 2,
  },
  advancedSettings: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  settingRow: {
    marginBottom: 16,
  },
  settingLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  numberSelector: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  numberOption: {
    flex: 1,
    backgroundColor: '#f9fafb',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingVertical: 8,
    marginHorizontal: 4,
    alignItems: 'center',
  },
  selectedNumberOption: {
    backgroundColor: '#3b82f6',
    borderColor: '#3b82f6',
  },
  numberOptionText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6b7280',
  },
  selectedNumberOptionText: {
    color: '#ffffff',
  },
  summaryCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  summaryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  summaryTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  summaryTypeTag: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 16,
  },
  summaryTypeText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: '600',
  },
  summaryDetails: {
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
    paddingTop: 16,
  },
  summaryDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  summaryDetailText: {
    marginLeft: 8,
    fontSize: 14,
    color: '#6b7280',
  },
  participantCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  participantHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  participantRole: {
    marginLeft: 8,
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  participantName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  participantTitle: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 2,
  },
  participantEmail: {
    fontSize: 13,
    color: '#9ca3af',
    marginTop: 2,
  },
  jobSummaryCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  jobSummaryTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  jobSummaryDepartment: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 2,
  },
  jobSummaryLocation: {
    fontSize: 13,
    color: '#9ca3af',
    marginTop: 2,
  },
  jobSummarySalary: {
    fontSize: 14,
    color: '#10b981',
    fontWeight: '600',
    marginTop: 4,
  },
  questionsList: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  questionSummaryItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  questionNumber: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#3b82f6',
    marginRight: 8,
    minWidth: 20,
  },
  questionSummaryText: {
    flex: 1,
    fontSize: 14,
    color: '#1f2937',
    lineHeight: 20,
  },
  additionalInfoCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  additionalInfoLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  additionalInfoText: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
  },
  settingsSummary: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  settingSummaryItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  settingSummaryText: {
    marginLeft: 12,
    fontSize: 14,
    color: '#1f2937',
    fontWeight: '500',
  },
  navigationButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: '#f9fafb',
  },
  backButtonText: {
    marginLeft: 4,
    fontSize: 16,
    color: '#6b7280',
    fontWeight: '500',
  },
  navigationSpacer: {
    flex: 1,
  },
  nextButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#3b82f6',
  },
  nextButtonText: {
    marginRight: 4,
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '600',
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#10b981',
  },
  savingButton: {
    backgroundColor: '#6b7280',
  },
  saveButtonText: {
    marginLeft: 4,
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '600',
  },
  disabledButton: {
    backgroundColor: '#d1d5db',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  modalCloseButton: {
    padding: 8,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  modalSaveButton: {
    padding: 8,
  },
  modalSaveText: {
    fontSize: 16,
    color: '#3b82f6',
    fontWeight: '600',
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  templateCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  templateHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  templateTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  templateTypeTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  templateTypeText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: '600',
  },
  templateDescription: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
    marginBottom: 8,
  },
  templateMeta: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  templateMetaText: {
    fontSize: 12,
    color: '#9ca3af',
    marginRight: 8,
  },
  interviewerCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 2,
    borderColor: '#e5e7eb',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  selectedInterviewerCard: {
    borderColor: '#3b82f6',
    backgroundColor: '#f0f9ff',
  },
  interviewerCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  interviewerCardInfo: {
    flex: 1,
  },
  interviewerCardName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  interviewerCardTitle: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 2,
  },
  interviewerCardDepartment: {
    fontSize: 12,
    color: '#9ca3af',
    marginTop: 2,
  },
  interviewerCardDetails: {
    marginBottom: 8,
  },
  interviewerCardDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  interviewerCardDetailText: {
    marginLeft: 8,
    fontSize: 13,
    color: '#6b7280',
  },
  interviewerExpertise: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    padding: 8,
  },
  expertiseLabel: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
    marginBottom: 4,
  },
  expertiseText: {
    fontSize: 13,
    color: '#1f2937',
  },
});

export default ScheduleInterviewScreen_FullyIntegrated;
